import React, { useState, useRef } from 'react';
import cx from 'classnames';
import RemoteSearchSelect from '../../molecules/RemoteSearchSelect';
import Input from '../../atoms/Input';
import { DefaultDecoration } from '../../molecules/InputField/InputField.decorators';
import Button from '../../atoms/Button';
import styles from './AddressLookup.module.scss';
import AddressLookupProps, {
  AddressApiItemResponseDto,
  AddressApiResponseDto,
  AddressInformation,
} from './AddressLookup.types';

const AddressLookup = ({
  searchUrl,
  secondarySearchUrl,
  regex = '^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([AZa-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9]?[A-Za-z])))) [0-9][A-Za-z]{2})$',
  countries = [],
  onChange,
  serviceCaller,
  dataPointer,
  value,
  dataCy = 'addressLookup',
  texts,
  errors,
  clearError,
  disableSearch = false,
  invalid,
  displayLabels,
  errorMsgHolderId,
}: AddressLookupProps) => {
  const [addressInformation, setAddressInformation] = useState<AddressInformation>(value ?? null);
  const [showAddressForm, setShowAddressForm] = useState<Boolean>(disableSearch);
  const initialLoad = useRef(true);
  const [manualAddress, setManualAddress] = useState<Boolean>(disableSearch);
  const [isValidEntry, setIsValidEntry] = useState<Boolean>(true);
  const componentErrors = (errors && errors[dataPointer]) || {};

  const selectProps = {
    searchUrl,
    keyName: 'text',
  };

  const setAddressInformationAndCallOnChange = (newValue: AddressInformation | null) => {
    setAddressInformation(newValue);

    if (!initialLoad.current || disableSearch) {
      onChange({
        value: newValue,
        dataPointer,
      });
    }
    initialLoad.current = false;
  };

  const formatSelection = (address: AddressApiItemResponseDto) => {
    if (address?.id) {
      // First comma is Line 1, anything subsequent is line 2.
      const [line1, ...restOfLine2] = [...address.text?.split(',')];
      // Last element is postcode, previous is the city name, anything previous is ignored.
      const cityAndPostcode = [...address.description?.split(',')];
      const line2 = restOfLine2.join('');
      const postCode = cityAndPostcode.pop();
      const city = cityAndPostcode.pop();

      const model: AddressInformation = {
        line1: line1.trim() ?? '',
        line2: line2.trim() ?? '',
        city: city.trim() ?? '',
        postCode: postCode.trim() ?? '',
        country: 'United Kingdom',
      };

      setAddressInformationAndCallOnChange(model);
      setShowAddressForm(true);
    }
  };

  const lookupPostcode = async (postcodeResponse: AddressApiResponseDto) => {
    const postcodeItem = postcodeResponse.items.find((item) => item.type === 'Postcode');

    if (postcodeItem) {
      const containerResponse: {
        data: AddressApiResponseDto;
      } = await serviceCaller({
        url: secondarySearchUrl,
        method: 'get',
        query: {
          container: postcodeItem.id,
        },
      });

      return containerResponse?.data?.items || [];
    }

    return [];
  };

  const handleFindByPostCodeClick = () => {
    setAddressInformationAndCallOnChange(null);
    setShowAddressForm(false);
    setManualAddress(false);

    if (clearError) {
      clearError(dataPointer);
    }
  };

  const handleChangePostCode = () => {
    setAddressInformationAndCallOnChange(null);
    setShowAddressForm(false);
  };

  const handleManualAddress = () => {
    setShowAddressForm(true);
    setManualAddress(true);

    if (clearError) {
      clearError(dataPointer);
    }
  };

  const handleChange = (e) => {
    if (e.target.value) {
      setAddressInformationAndCallOnChange({
        ...addressInformation,
        [e.target.name]: e.target.value,
      });
    } else {
      const clonedAddressInformation = { ...addressInformation };
      delete clonedAddressInformation[e.target.name];

      setAddressInformationAndCallOnChange(clonedAddressInformation);
    }
  };

  const handleRegexSuccess = (): void => setIsValidEntry(true);

  const handleRegexFailure = (): void => setIsValidEntry(false);

  const labelClasses: string = cx(styles.label);
  const EmptyOption = isValidEntry ? (
    <span>
      {texts.unknownAddressPt1}
      <Button dataCy='emptyOptionButton' color='secondary' variant='link' onClick={handleManualAddress}>
        {texts.unknownAddressPt2}
      </Button>
    </span>
  ) : (
    <span>{texts.pleaseUseCorrectFormat}</span>
  );

  // generate ids
  const searchErrorMsgHolderId = `${Math.floor(Math.random() * 1000) + 1}_errorMsg`;
  const line1IdErrorMsgHolderId = `${Math.floor(Math.random() * 1000) + 1}_errorMsg`;
  const line2IdErrorMsgHolderId = `${Math.floor(Math.random() * 1000) + 1}_errorMsg`;
  const cityErrorMsgHolderId = `${Math.floor(Math.random() * 1000) + 1}_errorMsg`;
  const postCodeErrorMsgHolderId = `${Math.floor(Math.random() * 1000) + 1}_errorMsg`;

  return (
    <div className={styles.root} aria-errormessage={errorMsgHolderId} data-cy={dataCy} data-pointer={dataPointer}>
      {manualAddress && !disableSearch && (
        <div className={styles.header}>
          <Button dataCy='findAddressByPostCodeButton' variant='link' onClick={handleFindByPostCodeClick}>
            {texts.findAddressByPostCode}
          </Button>
        </div>
      )}

      <div className={styles.content}>
        {!manualAddress && (
          <DefaultDecoration errorMessage={componentErrors[dataPointer]} errorMsgHolderId={searchErrorMsgHolderId}>
            <RemoteSearchSelect
              placeholder={texts.placeholders.searchBox}
              {...selectProps}
              valueToShow={addressInformation?.postCode}
              emptyOption={EmptyOption}
              queryParamName='postcode'
              additionalQueryParams={{ countries }}
              onChange={formatSelection}
              processResults={lookupPostcode}
              onChangeInputText={handleChangePostCode}
              serviceCaller={serviceCaller}
              regex={regex}
              onRegexSuccess={handleRegexSuccess}
              onRegexFailure={handleRegexFailure}
              autoComplete='off'
              invalid={invalid}
              ariaLabel={texts?.lookupAriaLabel}
              errorMsgHolderId={searchErrorMsgHolderId}
            />
            {!showAddressForm && (
              <Button dataCy='enterAddressManuallyButton' variant='link' onClick={handleManualAddress}>
                {texts.enterAddressManually}
              </Button>
            )}
          </DefaultDecoration>
        )}

        {showAddressForm ? (
          <>
            <DefaultDecoration
              errorMessage={componentErrors?.line1}
              title={displayLabels?.line1}
              labelClasses={labelClasses}
              errorMsgHolderId={line1IdErrorMsgHolderId}>
              <Input
                {...{
                  title: '',
                  name: 'line1',
                  type: 'string',
                  placeholder: texts.placeholders.line1,
                  value: addressInformation?.line1,
                  onChange: handleChange,
                  dataCy: `${dataCy}Line1`,
                  autoComplete: 'new-password',
                }}
                invalid={invalid}
                errorMsgHolderId={line1IdErrorMsgHolderId}
              />
            </DefaultDecoration>

            <DefaultDecoration
              errorMessage={componentErrors?.line2}
              title={displayLabels?.line2}
              labelClasses={labelClasses}
              errorMsgHolderId={line2IdErrorMsgHolderId}>
              <Input
                {...{
                  title: '',
                  name: 'line2',
                  type: 'string',
                  placeholder: texts.placeholders.line2,
                  value: addressInformation?.line2,
                  onChange: handleChange,
                  dataCy: `${dataCy}Line2`,
                  autoComplete: 'new-password',
                }}
                invalid={invalid}
                errorMsgHolderId={line2IdErrorMsgHolderId}
              />
            </DefaultDecoration>

            <DefaultDecoration
              errorMessage={componentErrors?.city}
              title={displayLabels?.city}
              labelClasses={labelClasses}
              errorMsgHolderId={cityErrorMsgHolderId}>
              <Input
                {...{
                  title: '',
                  name: 'city',
                  type: 'string',
                  placeholder: texts.placeholders.city,
                  value: addressInformation?.city,
                  onChange: handleChange,
                  dataCy: `${dataCy}City`,
                  autoComplete: 'new-password',
                }}
                invalid={invalid}
                errorMsgHolderId={cityErrorMsgHolderId}
              />
            </DefaultDecoration>
          </>
        ) : null}
        {manualAddress && (
          <DefaultDecoration
            errorMessage={componentErrors?.postCode || componentErrors[dataPointer]}
            title={displayLabels?.manualSearchBox}
            labelClasses={labelClasses}
            errorMsgHolderId={postCodeErrorMsgHolderId}>
            <Input
              {...{
                title: '',
                name: 'postCode',
                type: 'string',
                placeholder: texts.placeholders.manualSearchBox,
                value: addressInformation?.postCode,
                onChange: handleChange,
                dataCy: `${dataCy}PostCode`,
                autoComplete: 'new-password',
              }}
              invalid={invalid}
              errorMsgHolderId={postCodeErrorMsgHolderId}
            />
          </DefaultDecoration>
        )}
      </div>
    </div>
  );
};

export default AddressLookup;
